/** @file The store module for updating the file trees in the app. */
import { Left, Right } from 'purify-ts';
import Vue from 'vue';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
const moduleBuilder = storeBuilder.module('file_trees', {
    fileTrees: {},
    loaders: {},
});
/**
 * Convert a file tree to directory.
 *
 * @param data The data to convert to a file tree.
 * @returns Either a directory, or an error.
 */
function asDir(data) {
    return expr.match(data, 'type', {
        directory: dir => Right(dir),
        file: () => Left(new Error('File tree was not a directory, but a file.')),
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var FileTreeStore;
(function (FileTreeStore) {
    /**
     * Get the file tree.
     *
     * @param state The state of the file tree store.
     * @returns A function to retrieve the file tree.
     */
    // eslint-disable-next-line no-underscore-dangle
    const _getFileTree = (state) => 
    /**
     * Get the file tree.
     *
     * @param assignmentId The id of the assigment to get the file tree for.
     * @param submissionId The id of the submission to get the file tree for.
     * @returns The found file tree, or a nothing if it could not be found.
     */
    (assignmentId, submissionId) => Maybe.fromNullable(state.fileTrees[assignmentId]).chainNullable(assigTrees => assigTrees[submissionId]);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FileTreeStore.getFileTree = moduleBuilder.read(_getFileTree, 'getFileTree');
    const commitLoader = moduleBuilder.commit((state, { submissionId, loader }) => {
        Vue.set(state.loaders, submissionId, loader);
    }, 'commitLoader');
    const commitDeleteLoader = moduleBuilder.commit((state, { submissionId }) => {
        Vue.delete(state.loaders, submissionId);
    }, 'commitDeleteLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FileTreeStore.commitFileTree = moduleBuilder.commit((state, { assignmentId, submissionId, fileTree }) => {
        const assigTrees = state.fileTrees[assignmentId] ?? {};
        Vue.set(assigTrees, submissionId, fileTree);
        Vue.set(state.fileTrees, assignmentId, assigTrees);
    }, 'commitFileTree');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FileTreeStore.commitDeleteFileTree = moduleBuilder.commit((state, { assignmentId }) => {
        Vue.delete(state.fileTrees, assignmentId);
    }, 'commitDeleteFileTree');
    const commitDeleteAllFileTrees = moduleBuilder.commit(state => {
        Vue.set(state, 'fileTrees', {});
        Vue.set(state, 'loaders', {});
    }, 'commitDeleteAllFileTrees');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FileTreeStore.commitAutoTestTree = moduleBuilder.commit((state, { assignmentId, submissionId, autoTestTree }) => {
        const assigTrees = state.fileTrees[assignmentId];
        const oldTree = assigTrees?.[submissionId];
        if (oldTree != null) {
            Vue.set(assigTrees, submissionId, oldTree.addAutoTestTree(autoTestTree));
            Vue.set(state.fileTrees, assignmentId, assigTrees);
        }
    }, 'commitAutoTestTree');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FileTreeStore.commitDeleteAutoTestTree = moduleBuilder.commit((state, { assignmentId, submissionId }) => {
        const assigTrees = state.fileTrees[assignmentId];
        const oldTree = assigTrees?.[submissionId];
        if (oldTree != null) {
            Vue.set(assigTrees, submissionId, oldTree.removeAutoTestTree());
            Vue.set(state.fileTrees, assignmentId, assigTrees);
        }
    }, 'commitDeleteAutoTestTree');
    /** Action to load a file tree of a submission. */
    FileTreeStore.loadFileTree = moduleBuilder.dispatch(async (context, { assignmentId, submissionId, force = false }) => {
        const current = _getFileTree(context.state)(assignmentId, submissionId);
        if (!force && current.isJust()) {
            return;
        }
        if (force || context.state.loaders[submissionId] == null) {
            const loader = Promise.all([
                client.submission
                    .getRootFileTrees({ submissionId })
                    .then(x => x.unsafeCoerce()),
            ]).then(([{ cgResult: allFileTrees }]) => {
                commitDeleteLoader({ submissionId });
                const fileTree = models.FileTree.fromServerData(asDir(allFileTrees.student).unsafeCoerce(), allFileTrees.teacher.map(teacher => asDir(teacher).unsafeCoerce()));
                FileTreeStore.commitFileTree({
                    assignmentId,
                    submissionId,
                    fileTree,
                });
            }, err => {
                commitDeleteLoader({ submissionId });
                throw err;
            });
            commitLoader({ submissionId, loader });
        }
        await context.state.loaders[submissionId];
    }, 'loadFileTree');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    FileTreeStore.updateAutoTestTree = moduleBuilder.dispatch(async (_, payload) => {
        const { assignmentId, submissionId, autoTest, autoTestTree } = payload;
        await FileTreeStore.loadFileTree({ assignmentId, submissionId });
        const entries = [];
        autoTest.sets.forEach(set => {
            set.suites.forEach(suite => {
                utils.maybeUtils.and(suite.id, suite.rubricRow).ifJust(([suiteId, rubricRow]) => utils.getItem(autoTestTree, suiteId).ifJust(suiteEntries => {
                    entries.push(new models.AutoTestDirectoryServerData({
                        // The AutoTestDirectory class extends Directory<string>,
                        // but this is incorrect, as the first level subdirectories
                        // have an id of null.
                        id: null,
                        name: rubricRow.header,
                        entries: suiteEntries,
                        type: 'directory',
                    }, suiteId));
                }));
            });
        });
        FileTreeStore.commitAutoTestTree({
            assignmentId,
            submissionId,
            autoTestTree: {
                // @ts-expect-error: The AutoTestDirectory class
                // extends Directory<string>, but this is
                // incorrect, as the top level directory of the
                // AutoTest tree has an id of null.
                id: null,
                name: 'AutoTest generated files',
                entries,
            },
        });
    }, 'updateAutoTestTree');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitDeleteAllFileTrees();
    });
})(FileTreeStore || (FileTreeStore = {}));
